import 'cleave.js/dist/addons/cleave-phone.i18n';

import { computed, type ComputedRef, nextTick, type Ref, ref, watch } from 'vue';
import Cleave, { type CleaveOptions } from 'cleave.js';

export type FormatterType =
  | 'iban'
  | 'phone'
  | 'custom'
  | 'number'
  | 'currency'
  | 'percent'
  | 'squaremeter'
  | undefined;

type UseCleaveArgs = {
  /** Current formatter type (reactive) */
  formatterType: ComputedRef<FormatterType>;
  /** Number of decimal places (reactive) */
  decimals: ComputedRef<number>;
  /** Current locale (e.g., 'de-DE') (reactive) */
  locale: ComputedRef<string | undefined>;
  /** Is numeric? (reactive) */
  isNumeric: ComputedRef<boolean>;
  /** Getter that returns the actual <input> element (safe with PrimeVue wrappers) */
  getInputEl: () => HTMLInputElement | null;

  /** Model refs that the composable updates */
  rawValue: Ref<string>;
  viewValue: Ref<string>;
};

export function useCleaveController(args: UseCleaveArgs) {
  const cleaveInstance = ref<Cleave | null>(null);
  const cleaveActive = computed(() => !!cleaveInstance.value);

  // ALWAYS use German decimal marks
  function cleaveMarksForLocale() {
    // Always return German format: comma as decimal, dot as thousands separator
    return { decimalMark: ',', delimiter: '.' };
  }

  /** Convert raw (canonical with ".") to locale format for Cleave */
  function localizeRawForCleave(raw: string, loc?: string) {
    const { decimalMark } = cleaveMarksForLocale();
    return decimalMark === ',' ? raw.replace(/\./g, ',') : raw;
  }

  function buildCleaveOptions(): CleaveOptions | undefined {
    const ft = args.formatterType.value;
    const { decimalMark, delimiter } = cleaveMarksForLocale();
    const scale = args.decimals.value ?? 2;

    if (ft === 'iban') {
      return { blocks: [4, 4, 4, 4, 4, 4, 4, 2], delimiter: ' ', uppercase: true };
    }
    if (ft === 'phone') {
      // could be dynamic per region; keep DE for now
      return { phone: true, phoneRegionCode: 'DE' };
    }
    if (ft === 'number' || ft === 'currency' || ft === 'percent' || ft === 'squaremeter') {
      return {
        numeral: true,
        numeralDecimalScale: scale,
        numeralThousandsGroupStyle: 'thousand',
        numeralDecimalMark: decimalMark,
        delimiter,
        stripLeadingZeroes: true,
      } as CleaveOptions;
    }
    if (ft === 'custom') {
      // placeholder — extend as needed
      return { uppercase: true };
    }
    // no Cleave
    return undefined;
  }

  function destroyCleave() {
    cleaveInstance.value?.destroy();
    cleaveInstance.value = null;
  }

  function setupCleave() {
    const el = args.getInputEl();
    if (!el) return;

    const opts = buildCleaveOptions();
    // No Cleave needed → tear down existing instance
    if (!opts) {
      destroyCleave();
      return;
    }

    destroyCleave();
    cleaveInstance.value = new Cleave(el, {
      ...opts,
      onValueChanged: (e: {
        target: {
          value: string;
          rawValue: string;
        };
      }) => {
        const rawLocal = String(e?.target?.rawValue ?? '');
        // Convert Cleave raw (locale) to canonical:
        const { decimalMark } = cleaveMarksForLocale();
        const canonical =
          args.isNumeric.value && decimalMark === ',' ? rawLocal.replace(/,/g, '.') : rawLocal;

        args.rawValue.value = canonical; // Master: RAW (canonical)
        args.viewValue.value = e?.target?.value ?? ''; // Display value (formatted)
      },
    });

    // Immediately sync current raw into Cleave formatting
    applyRawToCleaveAndUpdateView();
  }

  /** Apply current RAW to Cleave (localized) and update the view */
  function applyRawToCleaveAndUpdateView() {
    const ci = cleaveInstance.value as any;
    if (!ci) return false;
    const raw = args.rawValue.value ?? '';
    const locRaw = args.isNumeric.value ? localizeRawForCleave(raw, args.locale.value) : raw;
    ci.setRawValue(locRaw);
    args.viewValue.value = ci.getFormattedValue?.() ?? locRaw;
    return true;
  }

  // Reinitialize Cleave on locale/decimals/type changes
  watch([args.locale, args.decimals, args.formatterType], () => nextTick(setupCleave));

  return {
    cleaveInstance,
    cleaveActive,
    setupCleave,
    destroyCleave,
    applyRawToCleaveAndUpdateView,
    cleaveMarksForLocale,
    localizeRawForCleave,
  };
}

/** Helper getter for PrimeVue Input/AutoComplete: returns the actual <input> */
export function getPrimeVueNativeInput(compRef: unknown): HTMLInputElement | null {
  const root = (compRef as any)?.$el as Element | null | undefined;
  if (!root) return null;

  // only accepts true <input>
  const el = root.querySelector('input');
  if (el instanceof HTMLInputElement) return el;

  // if the root itself is <input>
  if (root instanceof HTMLInputElement) return root;

  return null;
}
